/**
 * SEO Schema.org Structured Data Utilities
 * Provides JSON-LD schema generators for better search engine visibility
 */

// Always the one true canonical host, never `window.location.origin` —
// that reflected whichever host actually served the request (bare
// domain, www, ...), so schema URLs generated while someone browsed via
// a non-canonical host pointed right back at that same non-canonical
// host. Same fix/reasoning as SEOHead.jsx's canonical tag.
const siteUrl = 'https://cimonamia.com';
const siteName = 'Cimonamia';

/**
 * Organization Schema
 * Shows company information in search results
 */
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": siteName,
  "url": siteUrl,
  "logo": `${siteUrl}/logo.png`,
  "sameAs": [
    // Add social media profiles when available
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "Customer Service",
    "availableLanguage": ["en", "es"]
  }
});

/**
 * WebSite Schema with SearchAction
 * Enables site search box in Google results
 */
export const getWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": siteName,
  "url": siteUrl,
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${siteUrl}/shop?search={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
});

/**
 * Breadcrumb List Schema
 * Shows breadcrumb navigation in search results
 */
export const getBreadcrumbSchema = (items) => {
  if (!items || items.length === 0) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url ? `${siteUrl}${item.url}` : undefined
    }))
  };
};

/**
 * Product Schema (for ProductDetailPage)
 * Enhanced product information for search results
 */
export const getProductSchema = ({
  name,
  description,
  image,
  sku,
  brand,
  price,
  currency = 'USD',
  availability = 'in stock',
  condition = 'NewCondition',
  url,
  category,
  rating,
  reviewCount
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "image": image ? (Array.isArray(image) ? image : [image]) : [],
    "sku": sku,
    "brand": brand ? {
      "@type": "Brand",
      "name": brand
    } : undefined,
    "offers": {
      "@type": "Offer",
      "url": url ? `${siteUrl}${url}` : undefined,
      "priceCurrency": currency,
      "price": price,
      "availability": availability === 'in stock' 
        ? "https://schema.org/InStock" 
        : "https://schema.org/OutOfStock",
      "itemCondition": `https://schema.org/${condition}`
    }
  };

  // Add category if provided
  if (category) {
    schema.category = category;
  }

  // Add aggregate rating if available
  if (rating && reviewCount) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": rating,
      "reviewCount": reviewCount
    };
  }

  return schema;
};

/**
 * ItemList Schema (for Shop/Category pages)
 * Shows product listings in search results
 */
export const getItemListSchema = (products, listName = 'Products') => {
  if (!products || products.length === 0) return null;

  // Legacy pre-S3-migration images still store a bare relative path
  // (`/media/imported/...`, served by nginx as a static alias) rather than
  // an absolute CDN URL — structured data validators require a fully-
  // qualified image URL, so absolutize anything that isn't one already.
  const absImage = (path) => {
    if (!path) return undefined;
    return path.startsWith('http') ? path : `${siteUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": listName,
    "numberOfItems": products.length,
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "url": `${siteUrl}/product/${product.slug}`,
        // `ProductImage` never had a `.url` field (only original/thumbnail/
        // medium) — this was silently emitting no image for every single
        // product on every /shop and /shop/:category page, which is
        // exactly Search Console's "Merchant listings: missing field
        // image" report. Those fields are already absolute (served via
        // /api/media/serve/... or the CDN), so no extra prefixing needed.
        "image": absImage(product.images?.[0]?.original || product.images?.[0]?.medium || product.images?.[0]?.thumbnail),
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": product.currency || 'USD'
        }
      }
    }))
  };
};

/**
 * FAQPage Schema
 * Shows FAQ in search results with expandable answers
 */
export const getFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Article Schema (for blog posts/pages)
 */
export const getArticleSchema = ({
  title,
  description,
  image,
  datePublished,
  dateModified,
  author = siteName
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "image": image,
  "datePublished": datePublished,
  "dateModified": dateModified || datePublished,
  "author": {
    "@type": "Organization",
    "name": author
  },
  "publisher": {
    "@type": "Organization",
    "name": siteName,
    "logo": {
      "@type": "ImageObject",
      "url": `${siteUrl}/logo.png`
    }
  }
});

/**
 * CollectionPage Schema (for Category pages)
 */
export const getCollectionPageSchema = (categoryName, products) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": categoryName,
  "description": `Browse ${categoryName} products at ${siteName}`,
  "url": typeof window !== 'undefined' ? window.location.href : '',
  "mainEntity": getItemListSchema(products, categoryName)
});
